import { useEffect, useRef, type MouseEvent } from "react";
import { focusField, summaryTitle, type FieldIssue } from "./formErrors";

export interface ErrorSummaryProps {
  /** One issue per field, in on-screen order; each line links to its field. */
  issues: readonly FieldIssue[];
  /** Lines with no field on this form (unmet rules, unknown server fields): listed, not linked. */
  general?: readonly string[];
  /** A sentence above the list (a failed save's own words). */
  lead?: string | null;
  /** The control id of a field key. Default: the key itself. */
  idOf?: (field: string) => string;
  /** Moves focus to the summary each time it increases (a failed submit), never on a plain re-render. */
  focusSignal?: number;
  /** The heading when no field is named, e.g. "This action is not possible yet". */
  generalTitle?: string;
  /** Replaces "Please fix N fields" (the sign-in screens, which have their own bilingual heading). */
  title?: string;
  /**
   * Takes a line to its field when the field is not simply a control on screen (e.g. a workflow move, opened in its own
   * drawer). Without it a line focuses and scrolls to the control with the field's id.
   */
  onActivate?: (field: string) => void;
}

/**
 * The top of every form's error display (see `formErrors.ts`): "Please fix N fields", one link per field. A link
 * focuses its control and scrolls it into view inside the dialog's scroll area; keyboard users reach the links with
 * Tab from the summary, which takes focus after a failed submit. `role="alert"` announces it to screen readers.
 */
export function ErrorSummary({ issues, general = [], lead = null, idOf = (field) => field, focusSignal = 0, generalTitle = "This could not be saved", title, onActivate }: ErrorSummaryProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusSignal > 0) ref.current?.focus({ preventScroll: false });
  }, [focusSignal]);

  if (issues.length === 0 && general.length === 0 && !lead) return null;

  function go(event: MouseEvent<HTMLAnchorElement>, field: string) {
    event.preventDefault();
    if (onActivate) onActivate(field);
    else focusField(idOf(field));
  }

  return (
    <div className="gf-errsum" role="alert" tabIndex={-1} ref={ref} data-testid="error-summary">
      {/* A failure with nothing to list (offline, a conflict) is just its sentence, as before. */}
      {issues.length + general.length > 0 && <h3>{issues.length > 0 ? (title ?? summaryTitle(issues.length)) : generalTitle}</h3>}
      {lead && (issues.length + general.length > 0 ? <p>{lead}</p> : lead)}
      {issues.length + general.length > 0 && (
        <ul>
          {issues.map((issue) => (
            <li key={`field:${issue.field}`}>
              <a href={`#${idOf(issue.field)}`} data-field={issue.field} onClick={(event) => go(event, issue.field)}>
                {issue.message}
              </a>
            </li>
          ))}
          {general.map((line) => (
            <li key={`general:${line}`}>{line}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The inline message under a field; `id` is the control id (the message gets `<id>-error`, matching aria-describedby). */
export function FieldMessage({ id, message }: { id: string; message: string | undefined }) {
  return message ? (
    <span className="err" id={`${id}-error`}>
      {message}
    </span>
  ) : null;
}
